import React, { useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { useNavigateWithScrollMemory } from '../../hooks/useNavigateWithScrollMemory';
import defaultArticleThumbnail from '../../assets/default_article_thumbnail.svg';
import NewsSourceWithDate from '../common/NewsSourceWithDate';
import CommentCount from '../common/CommentCount';
import BookmarkCount from '../common/BookmarkCount';
import CardTitle from '../common/CardTitle';

const NewsVerticalCard = ({
  news,
  actionButton,
  coverActionButton,
  statsOnNewLine = false,
  referrerType,
}) => {
  const theme = useTheme();
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const [imageError, setImageError] = useState(false);
  const newsUrl = `/news/${news.slug || news.id}`;

  const handleLinkToNews = (e) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigateWithScrollMemory(newsUrl, referrerType ? { state: { referrerType } } : undefined);
    }
  };

  const handleError = () => {
    if (!imageError) setImageError(true);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: { xs: '16px', md: '12px', lg: '16px' },
        position: 'relative',
        minWidth: 0,
      }}
    >
      {/* 이미지 */}
      <Box
        component={RouterLink}
        to={newsUrl}
        state={referrerType ? { referrerType } : undefined}
        onClick={handleLinkToNews}
        sx={{ display: 'block', width: '100%', flexShrink: 0, position: 'relative' }}
      >
        <Box
          component="img"
          src={imageError || !news.imageUrl ? defaultArticleThumbnail : news.imageUrl}
          alt={imageError || !news.imageUrl ? 'Default article thumbnail' : news.title}
          onError={handleError}
          sx={{
            width: '100%',
            aspectRatio: '280 / 188',
            cursor: 'pointer',
            objectFit: 'cover',
            display: 'block',
            border: `1px solid ${theme.palette.grey.line}`,
          }}
        />
        {coverActionButton && (
          <Box
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
            sx={{
              position: 'absolute',
              top: { xs: 8, md: 10 },
              right: { xs: 8, md: 10 },
              zIndex: 3,
            }}
          >
            {coverActionButton}
          </Box>
        )}
      </Box>

      {/* 정보 영역 */}
      <Box
        sx={{
          flex: 1,
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* 제목 */}
        <CardTitle
          title={news.title}
          to={newsUrl}
          onClick={handleLinkToNews}
          sx={{ fontSize: { xs: 16, md: 14, lg: 16 }, fontWeight: 'bold' }}
        />

        <NewsSourceWithDate
          linkUrl={news.linkUrl}
          source={news.sourceUrl}
          fontSize={14}
          sx={{ mt: { xs: '12px', md: '12px', lg: '16px' } }}
        />

        {/* 하단 정보 + actionButton */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
          }}
        >
          <Box>
            {statsOnNewLine ? (
              <>
                {news.releaseDate && (
                  <Typography
                    fontSize={14}
                    sx={{
                      color: theme.palette.black[4],
                      whiteSpace: 'nowrap',
                      mt: { xs: '16px', md: '12px', lg: '16px' },
                    }}
                  >
                    {news.releaseDate}
                  </Typography>
                )}
                <Box
                  display="flex"
                  gap={1.5}
                  alignItems="center"
                  sx={{ mt: { xs: '16px', md: '12px', lg: '16px' } }}
                >
                  <CommentCount commentCount={news.commentCount} />
                  <BookmarkCount bookmarkCount={news.bookmarkCount} />
                </Box>
              </>
            ) : (
              <Box
                display="flex"
                gap={1}
                alignItems="center"
                sx={{ mt: { xs: '16px', md: '12px', lg: '16px' } }}
              >
                {news.releaseDate && (
                  <>
                    <Typography
                      fontSize={14}
                      sx={{ color: theme.palette.black[4], whiteSpace: 'nowrap' }}
                    >
                      {news.releaseDate}
                    </Typography>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        height: '8px',
                        alignSelf: 'center',
                        backgroundColor: theme.palette.grey.line,
                      }}
                    />
                  </>
                )}
                <Box display="flex" gap={{ xs: 1, md: 1.5 }} alignItems="center">
                  <CommentCount commentCount={news.commentCount} />
                  <BookmarkCount bookmarkCount={news.bookmarkCount} />
                </Box>
              </Box>
            )}
          </Box>

          {/* 우측: actionButton */}
          {actionButton}
        </Box>

        {/* 본문 (xs only) */}
        <Typography
          fontSize={14}
          color={theme.palette.black[6]}
          sx={{
            display: { xs: '-webkit-box', md: 'none' },
            mt: '16px',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            lineHeight: 1.5,
          }}
        >
          {news.content}
        </Typography>
      </Box>
    </Box>
  );
};

export default NewsVerticalCard;
